// eslint-disable-next-line custom/no-restricted-imports
import { path, isEmpty } from 'ramda';
import type { Reducer } from 'redux';
import type { FetcherSelectorState } from '@peloton/redux-fetch/redux';
import type { TypedAction } from '@peloton/redux/TypedAction';
import { isDefined } from '@peloton/types/isDefined';
import type { AchievementTemplate } from '@engage/achievements/Achievement';
import type { MemberSelectorState } from '@engage/members/selectors';
import { getMember } from '@engage/members/selectors';
import { getFitnessDiscipline } from '@engage/metadata/redux';
import type { ActiveDayHistory } from '@engage/overview-ui/models';
import type { Subscription } from '@engage/subscriptions/Subscription';
import { isDeviceSub } from '@engage/subscriptions/Subscription';
import type { Overview } from '../../models/Overview';
import type { PersonalRecords } from '../../models/PersonalRecords';
import type { Streaks } from '../../models/Streaks';
import type { WorkoutCounts } from '../../models/WorkoutCounts';
import { toWorkoutCountsView } from '../../workoutCountsMappers';
import type { OverviewSelectorState, State } from './types';

type Action =
  | TypedAction<ReturnType<typeof loadOverviewSuccess>, OverviewActionType.Success>
  | TypedAction<
      ReturnType<typeof loadActiveDayHistorySuccess>,
      OverviewActionType.ActiveDayHistorySuccess
    >;

export enum OverviewActionType {
  Success = 'pelo/overview/RequestSuccess',
  ActiveDayHistorySuccess = '@members/pg-preferences/overview/ACTIVE_DAY_HISTORY',
}

// Re-export OVERVIEW_NAMESPACE from types for backwards compatibility
export { OVERVIEW_NAMESPACE } from './types';

const RESET_PR_VALUE = -1;

// reducers
export const overviewReducer: Reducer<State> = (state = {}, action: Action) => {
  switch (action.type) {
    case OverviewActionType.Success:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          achievements: action.payload.achievements,
          id: action.payload.id,
          personalRecords: action.payload.personalRecords,
          workoutCounts: action.payload.workoutCounts,
          firstPartyOrPartner3pWorkoutCounts:
            action.payload.firstPartyOrPartner3pWorkoutCounts,
          streaks: action.payload.streaks,
        },
      };
    case OverviewActionType.ActiveDayHistorySuccess:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          activeDayHistory: action.payload.activeDayHistory,
          activeDayHistoryNonPartner3p: action.payload.activeDayHistoryNonPartner3p,
        },
      };
    default:
      return state;
  }
};

// selectors
export const getWorkoutCounts = (
  state: OverviewSelectorState & MemberSelectorState,
  usernameOrId?: string,
) => {
  const member = getMember(state, usernameOrId);
  if (!isDefined(member)) {
    return undefined;
  }
  const counts = path<WorkoutCounts>(['overview', member.id, 'workoutCounts'], state);

  return isDefined(counts) ? toWorkoutCountsView(counts, member) : undefined;
};

export const getfirstPartyOrPartner3pWorkoutCounts = (
  state: OverviewSelectorState & MemberSelectorState,
  usernameOrId?: string,
) => {
  const member = getMember(state, usernameOrId);
  if (!isDefined(member)) {
    return undefined;
  }
  const counts = path<WorkoutCounts>(
    ['overview', member.id, 'firstPartyOrPartner3pWorkoutCounts'],
    state,
  );

  return isDefined(counts) ? toWorkoutCountsView(counts, member) : undefined;
};

export const getAchievementsForOverview = (
  state: OverviewSelectorState & MemberSelectorState,
  usernameOrId?: string,
) => {
  const member = getMember(state, usernameOrId);
  if (!isDefined(member)) {
    return undefined;
  }
  const achievements = path<AchievementTemplate[]>(
    ['overview', member.id, 'achievements'],
    state,
  );

  return achievements;
};

export const getActiveDayHistory = (state: OverviewSelectorState, userId: string) =>
  path<ActiveDayHistory>(['overview', userId, 'activeDayHistory'], state) ?? [];

export const getActiveDayHistoryNonPartner3p = (
  state: OverviewSelectorState,
  userId: string,
) =>
  path<ActiveDayHistory>(['overview', userId, 'activeDayHistoryNonPartner3p'], state) ??
  [];

export const getStreaks = (state: OverviewSelectorState, userId: string) =>
  path<Streaks>(['overview', userId, 'streaks'], state) ?? {
    bestWeekly: 0,
    currentWeekly: 0,
    startDateOfCurrentWeekly: 0,
  };

// Reset PRs remain in the response but with a negative output value
export const getFilteredPersonalRecords = (personalRecords: PersonalRecords) =>
  personalRecords.map(category => ({
    ...category,
    records: category.records.filter(record => record.value !== RESET_PR_VALUE),
  }));

export const getPersonalRecords = (
  state: OverviewSelectorState & MemberSelectorState & FetcherSelectorState,
  usernameOrId?: string,
) => {
  const member = getMember(state, usernameOrId);
  if (!isDefined(member)) {
    return [];
  }
  const counts =
    path<PersonalRecords>(['overview', member.id, 'personalRecords'], state) ?? [];
  const filteredCounts = getFilteredPersonalRecords(counts);

  return filteredCounts
    .filter(category => category.records.length > 0)
    .map(category => {
      const fitnessDiscipline = getFitnessDiscipline(state, category.slug);
      return {
        ...category,
        name: fitnessDiscipline && fitnessDiscipline.name,
      };
    });
};

export const hasDeviceSub = (subs: Subscription[]) => {
  return !isEmpty(subs.filter(sub => isDeviceSub(sub)));
};

// actions
export const loadOverviewSuccess = (overview: Overview) => ({
  type: OverviewActionType.Success,
  payload: overview,
});

export const loadActiveDayHistorySuccess = (
  userId: string,
  activeDayHistory: ActiveDayHistory,
  activeDayHistoryNonPartner3p: ActiveDayHistory = [],
) => ({
  type: OverviewActionType.ActiveDayHistorySuccess,
  payload: {
    id: userId,
    activeDayHistory,
    activeDayHistoryNonPartner3p,
  },
});

const REQUIRED_OVERVIEW_KEYS = [
  'achievements',
  'activeDayHistory',
  'personalRecords',
  'workoutCounts',
].filter(Boolean);

const hasRequiredOverviewProps = (overview: Overview): boolean =>
  REQUIRED_OVERVIEW_KEYS.every(k => {
    // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Overview'
    return Boolean(overview[k]);
  });

export const isOverviewLoaded = (
  state: OverviewSelectorState & MemberSelectorState,
  usernameOrId?: string,
) => {
  const member = getMember(state, usernameOrId);
  if (!isDefined(member)) {
    return false;
  }
  const memberOverview = path<Overview>(['overview', member.id], state);
  return (
    isDefined(memberOverview) && hasRequiredOverviewProps(memberOverview as Overview)
  );
};
